import { createApi } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../auth';
import { APIVersion } from '../../constants/keys';
import { NoteApi } from '../../types/notes';

const transformErrorResponse = (response: FetchBaseQueryError) =>
  (response as { data?: { errors?: unknown } }).data?.errors;

interface NoteArgs {
  locationId: string;
  date: string;
  content: string;
  noteType: string;
}

interface EditNoteArgs extends NoteArgs {
  noteId: string;
}

interface GetNotesArgs {
  location_id?: string;
  ordering?: string;
  page?: number;
  per_page?: number;
}

interface GetNotesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: NoteApi[];
}

// Notes
export const notesApi = createApi({
  reducerPath: 'notesApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    createNote: builder.mutation<NoteApi, NoteArgs>({
      query: ({ locationId, date, content, noteType }) => ({
        url: `/core-api/${APIVersion}/notes`,
        method: 'POST',
        body: { location_id: locationId, date, content, note_type: noteType },
      }),
      transformErrorResponse,
    }),
    getNotes: builder.query<NoteApi[], GetNotesArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/notes?ordering=date`,
        params,
      }),
      transformResponse: (data: GetNotesResponse) => data.results,
      transformErrorResponse,
    }),
    editNote: builder.mutation<NoteApi, EditNoteArgs>({
      query: ({ noteId, locationId, date, content, noteType }) => ({
        url: `/core-api/${APIVersion}/notes/${noteId}`,
        method: 'PATCH',
        body: { location_id: locationId, date, content, note_type: noteType },
      }),
      transformErrorResponse,
    }),
    deleteNote: builder.mutation<void, { noteId: string }>({
      query: ({ noteId }) => ({
        url: `/core-api/${APIVersion}/notes/${noteId}`,
        method: 'DELETE',
      }),
      transformErrorResponse,
    }),
  }),
});

export const { useCreateNoteMutation, useGetNotesQuery, useEditNoteMutation, useDeleteNoteMutation } = notesApi;
